import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import React, { HTMLAttributes, useEffect, useRef, useState } from 'react'
import { Virtuoso } from 'react-virtuoso'
import { TextInput } from '@/components/widget-components'
import { useSignupStore } from '@/reducers'
import occupationList from '@/utils/locales/en/occupations.json'
import occupationListFR from '@/utils/locales/fr/occupations_fr.json'

const CustomList = React.forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ style, ...props }, ref) => {
  return (
    <div
      style={{
        ...style,
      }}
      className="border border-gray-300 bg-white dark:bg-pebble"
      ref={ref}
      {...props}
      data-testid="form-occupationselector"
    />
  )
})
CustomList.displayName = 'CustomList'
interface OccupationSelectorProps {
  value: string
  onChange: (value: string) => void
}
export const OccupationSelector: React.FC<OccupationSelectorProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation()
  const occupations =
    localStorage.getItem('lang') === 'fr'
      ? occupationListFR.OCCUPATIONS
      : occupationList.OCCUPATIONS
  const [showDropdown, setShowDropdown] = useState(false)
  const [searchedValue, setSearchedValue] = useState('')
  const [filteredOccupations, setFilteredOccupations] = useState<string[]>([])
  const setOccupation = useSignupStore.use.setOccupation()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const handleClickOutside = (e: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target as Node)
    ) {
      setShowDropdown(false)
    }
  }
  const handleOccupationsFiltering = (value: string) => {
    const trimmedValue = value.toLowerCase().trim()
    if (trimmedValue) {
      const filtered = occupations.filter((item) =>
        item.toLowerCase().includes(trimmedValue)
      )
      setFilteredOccupations([...filtered, t('occupation.list.other')])
    } else {
      setFilteredOccupations([])
    }
  }
  useEffect(() => {
    // Attach click event listener to document to handle clicks outside of the dropdown
    document.addEventListener('click', handleClickOutside)
    return () => {
      // Remove click event listener when component unmounts
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])
  return (
    <>
      <div
        className="relative"
        ref={dropdownRef}
        data-testid="form-occupationselector"
      >
        <TextInput
          placeholder={t('occupation.placeholder')}
          value={value}
          onChange={(e) => {
            const inputValue = e.target.value
            onChange(inputValue)
            handleOccupationsFiltering(inputValue)
            setShowDropdown(!!inputValue.trim())
          }}
          onFocus={() => {
            if (value.trim()) setShowDropdown(true)
          }}
          data-testid="select-form-field-occupation"
        />
        {showDropdown && (
          <Virtuoso
            components={{
              List: CustomList,
            }}
            style={{
              height: 342,
              width: '100%',
              position: 'absolute',
            }}
            totalCount={filteredOccupations.length}
            data-testid="form-occupationselector"
            itemContent={(index) => (
              <div
                key={filteredOccupations[index]}
                onClick={() => {
                  onChange(filteredOccupations[index])
                  setShowDropdown(false)
                }}
                className={clsx(
                  `hover:bg-rock min-w-[150px] justify-between p-2 cursor-pointer`
                )}
                data-testid={`occupation-selector-item-${filteredOccupations[index].replace(/\s+/g, '-').toLowerCase()}`}
              >
                {filteredOccupations[index]}
              </div>
            )}
          />
        )}
      </div>
    </>
  )
}

/* 
Potential improvements:
1. Add proper keyboard navigation for accessibility
2. Add aria-expanded and aria-controls attributes
3. Implement better loading states for large occupation lists
4. Add error state and validation for invalid occupation selections
5. Consider grouping occupations by category for easier selection
6. Improve mobile experience with touch-friendly UI
7. Add clear button to easily reset the input
8. Add autocomplete functionality with more intelligent matching
*/
